// libraries
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Avatar, Badge, Button, Dropdown, Menu, notification, Select, Tooltip, Typography } from 'antd';
import { DownOutlined, TeamOutlined, UserOutlined } from '@ant-design/icons';
import { blue } from '@ant-design/colors';

// utils
import { txt } from '../../../../locales/i18n';
import { formatDate } from '../../../../utils/date';
import { useDeviceSize } from '../../../../hooks/device-size';

// services
import * as ConversationService from '../../../../services/bb-api/conversations';

const statuses: Record<string, any>[] = [
	{ name: 'Slettet af admin', id: -4 },
	{ name: 'Slettet af bruger', id: -3 },
	{ name: 'Aktiveret af bruger', id: 1 },
	{ name: 'Aktiveret af admin', id: 2 },
];

export type ConversationHeaderProps = {
	username: string;
	userId: number;
	userMunicipality: string;

	creatorUsername: string;
	creatorId: number;
	creatorMunicipality: string;

	created: Date;
	statusId: number;

	memberAmount: number;
	messageAmount: number;

	members: Record<string, any>[];
};

export function ConversationHeader(props: ConversationHeaderProps): JSX.Element {
	const [loading, setLoading] = useState<boolean>(false);
	const isMobileDevice: boolean = useDeviceSize();
	const { id } = useParams<Record<string, any>>();

	function getTooltipTitle(): JSX.Element {
		return (
			<span>
				{`${txt('global.messages')} ${props.messageAmount}`}
				<br />
				{`${txt('global.members')} ${props.memberAmount}`}
			</span>
		);
	}

	async function handleStatusChange(status: number): Promise<void> {
		setLoading(true);

		try {
			await ConversationService.update(id, status);
			notification.success({ message: txt('notification.update successful') });
		} catch (error) {
			notification.error({ message: txt('notification.update failed') });
		}

		setLoading(false);
	}

	const userDropdownMenu: JSX.Element = (
		<Menu defaultSelectedKeys={[String(props.creatorId)]}>
			{props.members.map((user: Record<string, any>) => (
				<Menu.Item key={user.id} icon={<UserOutlined />}>
					<Link to={`/users/${user.id}`}>{user.username}</Link>
				</Menu.Item>
			))}
		</Menu>
	);

	return (
		<StyledGroupHeader isMobileDevice={isMobileDevice}>
			<div className="information-container">
				<Tooltip title={getTooltipTitle()}>
					<Badge count={props.messageAmount} color="blue">
						<Avatar size={48} src={'group.image'}>
							{props.memberAmount} <TeamOutlined />
						</Avatar>
					</Badge>
				</Tooltip>

				<div className="details-container">
					<h3>
						<Link to={`/users/${props.userId}`} className="user-details-container">
							<span>{props.username}</span>
							<Typography.Text type="secondary">{props.userMunicipality}</Typography.Text>
						</Link>
					</h3>

					<Tooltip title={`${txt('global.created by')} ${props.creatorUsername}`}>
						<Link to={`/users/${props.creatorId}`} className="user-details-container">
							<span>{props.creatorUsername}</span>
							<Typography.Text type="secondary">{props.creatorMunicipality}</Typography.Text>

							<Typography.Text ellipsis type="secondary">
								{formatDate(props.created)}
							</Typography.Text>
						</Link>
					</Tooltip>
				</div>
			</div>

			<div className="select-container">
				<Tooltip title="Status">
					<Select loading={loading} defaultValue={props.statusId} onChange={handleStatusChange} dropdownMatchSelectWidth={false} bordered={false}>
						{statuses.map((status: Record<string, any>) => (
							<Select.Option key={status.id} value={status.id}>
								{status.name}
							</Select.Option>
						))}
					</Select>
				</Tooltip>

				<Tooltip title={txt('global.members')}>
					<Dropdown overlay={userDropdownMenu}>
						<Button>
							{props.creatorUsername}
							<DownOutlined />
						</Button>
					</Dropdown>
				</Tooltip>
			</div>
		</StyledGroupHeader>
	);
}

export type StyledGroupHeaderProps = {
	isMobileDevice: boolean;
};

const StyledGroupHeader = styled.section<StyledGroupHeaderProps>`
	display: flex;
	flex-direction: ${({ isMobileDevice }) => (isMobileDevice ? 'column' : 'row')};
	align-items: center;
	gap: 1em;

	.information-container {
		display: flex;
		align-items: center;
		gap: 1em;
	}

	.details-container {
		display: flex;
		flex-direction: column;

		h3 {
			margin: 0;
			display: flex;
			gap: 1ch;
		}

		.user-details-container {
			color: ${blue.primary};
			display: flex;
			flex-wrap: wrap;
			gap: 0 1ch;
		}
	}

	.select-container {
		margin-left: ${({ isMobileDevice }) => (isMobileDevice ? '0' : 'auto')};
		display: flex;
		gap: 1rem;
		align-self: center;
	}
`;
